import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Copy, Gift, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { WAITLIST } from "@/constants/testIds";

const ease = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read ?ref=CODE from URL once
function getRefFromUrl() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("ref");
  if (!raw) return null;
  return raw.trim().toUpperCase().slice(0, 16) || null;
}

export default function Waitlist() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [refFromUrl, setRefFromUrl] = useState(null);
  const [referralData, setReferralData] = useState(null); // { referral_code, referral_count }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setRefFromUrl(getRefFromUrl());
  }, []);

  // Poll referral count every 20s after signup (so user sees friends joining)
  useEffect(() => {
    if (!referralData?.referral_code) return;
    const code = referralData.referral_code;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/waitlist/referral/${code}`);
        setReferralData((prev) =>
          prev ? { ...prev, referral_count: data.referral_count } : prev
        );
      } catch (_) {
        /* silent */
      }
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [referralData?.referral_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!firstName.trim()) {
      setErrorMsg("Te rugăm să introduci prenumele.");
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Te rugăm să introduci un email valid.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        ref: refFromUrl || undefined,
      });
      setReferralData({
        referral_code: data.referral_code,
        referral_count: data.referral_count ?? 0,
      });
      setSubmitted(true);
      toast.success("Ești pe listă. Mulțumim!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "A apărut o eroare. Te rugăm să încerci din nou.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const referralUrl = referralData
    ? `${window.location.origin}/?ref=${referralData.referral_code}#waitlist`
    : "";

  const handleCopy = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Link copiat.");
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error("Nu am putut copia linkul.");
    }
  };

  const shareText = `Am rezervat acces prioritar la EILEA Body Milk Mist — un body milk mist inspirat din skincare. Intră și tu pe lista de pre-lansare:`;
  const whatsappUrl = referralUrl
    ? `https://wa.me/?text=${encodeURIComponent(`${shareText} ${referralUrl}`)}`
    : "#";

  return (
    <section
      id="waitlist"
      data-testid={WAITLIST.section}
      className="relative py-28 md:py-44 bg-[#2C2826] text-[#FDFBF7] overflow-hidden"
    >
      {/* soft cream radial */}
      <div
        className="absolute inset-0 -z-0 opacity-[0.08]"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, #FDFBF7 0%, transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-[860px] px-6 md:px-12 text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="overline text-[#FDFBF7]/70 mb-12"
        >
          — Lista de lansare
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease, delay: 0.05 }}
          className="font-display text-[1.35rem] sm:text-[1.5rem] md:text-[1.7rem] lg:text-[1.8rem] leading-[1.25] tracking-[-0.005em]"
        >
          Acces prioritar la primul drop
          <span className="font-logo tracking-[0.22em] align-baseline ml-3 md:ml-4">
            eilea
          </span>
          .
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease, delay: 0.2 }}
          className="mt-8 max-w-xl mx-auto text-base md:text-[1.05rem] leading-relaxed text-[#FDFBF7]/75 font-light"
        >
          Primești acces anticipat, informații despre lansare și
          disponibilitatea primului drop.
        </motion.p>

        {refFromUrl && !submitted && (
          <motion.div
            data-testid={WAITLIST.referralBadge}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.3 }}
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 border border-[#E9D8CB]/40 text-[#E9D8CB] text-[11px] tracking-[0.22em] uppercase"
          >
            <Gift className="w-3.5 h-3.5" strokeWidth={1.4} />
            Invitație de la un prieten
          </motion.div>
        )}

        <div className="mt-14 max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                data-testid={WAITLIST.form}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.7, ease }}
                className="text-left"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
                  <div className="flex flex-col">
                    <label
                      htmlFor="first_name"
                      className="overline text-[#FDFBF7]/60 mb-3"
                    >
                      Prenume
                    </label>
                    <input
                      id="first_name"
                      type="text"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      data-testid={WAITLIST.inputFirstName}
                      placeholder=""
                      className="bg-transparent border-b border-[#FDFBF7]/30 py-3 text-lg text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#FDFBF7] transition-colors"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor="email"
                      className="overline text-[#FDFBF7]/60 mb-3"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid={WAITLIST.inputEmail}
                      placeholder=""
                      className="bg-transparent border-b border-[#FDFBF7]/30 py-3 text-lg text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#FDFBF7] transition-colors"
                    />
                  </div>
                </div>

                {errorMsg && (
                  <p
                    data-testid={WAITLIST.error}
                    className="mt-6 text-sm text-[#E9D8CB]"
                  >
                    {errorMsg}
                  </p>
                )}

                <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid={WAITLIST.submit}
                    className="btn-ghost-on-dark group disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Se trimite..." : "Vreau acces prioritar"}
                    {!loading && (
                      <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
                    )}
                  </button>

                  <p className="text-[11px] text-[#FDFBF7]/55 leading-relaxed max-w-[18rem]">
                    Primele stocuri vor fi limitate.
                  </p>
                </div>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                data-testid={WAITLIST.success}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease }}
                className="border border-[#FDFBF7]/20 px-6 sm:px-10 py-12 sm:py-14 flex flex-col items-center text-center"
              >
                <span className="inline-flex items-center justify-center w-14 h-14 border border-[#E9D8CB] mb-6">
                  <Check className="w-5 h-5 text-[#E9D8CB]" strokeWidth={1.4} />
                </span>
                <h3 className="font-serif-classic text-2xl md:text-3xl text-[#FDFBF7]">
                  Ești pe listă, {firstName || "drăguțo"}.
                </h3>
                <p className="mt-4 max-w-md text-sm md:text-base text-[#FDFBF7]/70 leading-relaxed font-light">
                  Locul tău pe lista de acces prioritar este rezervat.
                  Vei fi printre primele persoane care vor afla când
                  lansăm primul drop EILEA.
                </p>

                {/* ───────── Referral block ───────── */}
                {referralData && (
                  <div
                    data-testid={WAITLIST.referralBlock}
                    className="mt-12 w-full max-w-md mx-auto pt-10 border-t border-[#FDFBF7]/15"
                  >
                    <h4 className="font-serif-classic text-xl md:text-2xl text-[#FDFBF7] leading-snug">
                      Distribuie EILEA. Avansezi pe listă.
                    </h4>
                    <p className="mt-3 text-[13px] md:text-sm text-[#FDFBF7]/65 leading-relaxed font-light">
                      Pentru fiecare persoană care se înscrie prin linkul
                      tău, urci în lista de acces prioritar pentru primul
                      drop EILEA.
                    </p>

                    {referralData.referral_count > 0 ? (
                      <>
                        <div className="mt-9 flex items-start justify-center gap-3">
                          <ArrowUp
                            className="w-4 h-4 text-[#E9D8CB] mt-3"
                            strokeWidth={1.4}
                            aria-hidden="true"
                          />
                          <span
                            data-testid={WAITLIST.referralCount}
                            className="font-serif-classic text-[2.8rem] md:text-[3.2rem] leading-none text-[#FDFBF7] tabular-nums"
                          >
                            {referralData.referral_count}
                          </span>
                          <span className="overline text-[#FDFBF7]/55 mt-3">
                            {referralData.referral_count === 1
                              ? "invitație confirmată"
                              : "invitații confirmate"}
                          </span>
                        </div>
                        <p className="mt-3 text-[12px] md:text-[12.5px] text-[#FDFBF7]/55 leading-relaxed font-light italic">
                          Locul tău urcă cu fiecare înscriere nouă.
                        </p>
                      </>
                    ) : (
                      <p className="mt-8 text-[13px] md:text-sm text-[#FDFBF7]/70 leading-relaxed font-light">
                        Distribuie linkul tău pentru a avansa pe lista de
                        acces prioritar.
                      </p>
                    )}

                    {/* Link + copy */}
                    <div className="mt-8 flex items-stretch border border-[#FDFBF7]/25">
                      <div
                        data-testid={WAITLIST.referralLink}
                        className="flex-1 px-4 py-3 text-[12px] md:text-[13px] text-[#FDFBF7]/85 truncate text-left font-mono tracking-tight"
                        title={referralUrl}
                      >
                        {referralUrl.replace(/^https?:\/\//, "")}
                      </div>
                      <button
                        type="button"
                        onClick={handleCopy}
                        data-testid={WAITLIST.referralCopy}
                        className="px-4 sm:px-5 border-l border-[#FDFBF7]/25 text-[11px] tracking-[0.2em] uppercase text-[#FDFBF7] hover:bg-[#FDFBF7]/10 transition-colors flex items-center gap-2"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3.5 h-3.5" strokeWidth={1.6} />
                            Copiat
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" strokeWidth={1.4} />
                            Copiază
                          </>
                        )}
                      </button>
                    </div>

                    {/* Share buttons */}
                    <div className="mt-5 flex items-center justify-center gap-3">
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={WAITLIST.referralWhatsapp}
                        className="px-5 py-2.5 border border-[#FDFBF7]/30 text-[11px] tracking-[0.22em] uppercase text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#2C2826] transition-colors"
                      >
                        WhatsApp
                      </a>
                      <button
                        type="button"
                        onClick={handleCopy}
                        data-testid={WAITLIST.referralInstagram}
                        className="px-5 py-2.5 border border-[#FDFBF7]/30 text-[11px] tracking-[0.22em] uppercase text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#2C2826] transition-colors"
                      >
                        Instagram
                      </button>
                    </div>

                    <p className="mt-6 text-[10.5px] tracking-[0.12em] uppercase text-[#FDFBF7]/40">
                      Codul tău · {referralData.referral_code}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
